import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AssetTypeModel } from '@app/core/models/assets/asset-type.model';
import { AssetModel } from '@app/core/models/assets/asset.model';
import { GroupTypeModel } from '@app/core/models/groups/group-type.model';
import { GroupModel } from '@app/core/models/groups/group.model';
import { PopUpMode } from '@app/core/models/popup-mode.model';
import { RecipientEntityModel } from '@app/core/models/recipient-entity/recipient-entity.model';
import { ReferentialModel } from '@app/core/models/referentials/referential.model';
import { ReferentialsModel } from '@app/core/models/referentials/referentials.model';
import { AssetService } from '@app/core/services/asset.service';
import { GroupService } from '@app/core/services/group.service';
import { ModalComponent } from '@app/shared/components/modal/modal.component';
import { ModalHelper } from '@app/shared/models/modal-helper.model';
import { MultiSelectModel } from '@app/shared/models/multi-select.model';
import * as _ from 'lodash';
import { IDropdownSettings } from 'ng-multiselect-dropdown';
import { Observable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { AdministrationDetailBase } from '../../administration-detail-base';

@Component({
  selector: 'app-asset-detail',
  templateUrl: './asset-detail.component.html',
  styleUrls: ['./asset-detail.component.scss'],
})
export class AssetDetailComponent
  extends AdministrationDetailBase
  implements OnInit, OnDestroy
{
  asset: AssetModel;
  assetTypes: ReferentialModel[] = [];
  selectedItemsAssetTypes: ReferentialModel[] = [];
  assetTypesDropDownSettings: IDropdownSettings;
  operationalGroups: GroupModel[] = [];
  selectedItemsOperationalGroups: GroupModel[] = [];
  operationalGroupsDropDownSettings: IDropdownSettings;
  recipientEntities: RecipientEntityModel[] = [];
  selectedItemsRecipientEntities: GroupModel[] = [];
  recipientEntitiesGroupsDropDownSettings: IDropdownSettings;
  isCreateMode: boolean;
  submitted: boolean = false;

  constructor(
    groupService: GroupService,
    private route: ActivatedRoute,
    private router: Router,
    private assetService: AssetService,
    public dialog: MatDialog
  ) {
    super(groupService);
    this.asset = new AssetModel();
  }

  ngOnInit(): void {
    this.operationalGroupsDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'Name',
      true,
      true,
      true,
      false
    );
    this.operationalGroupsDropDownSettings.allowRemoteDataSearch = true;
    this.recipientEntitiesGroupsDropDownSettings =
      new MultiSelectModel().getSettings('Id', 'Name', true, true, true, false);
    this.assetTypesDropDownSettings = new MultiSelectModel().getSettings(
      'Id',
      'Label',
      true,
      true,
      true,
      false
    );

    this.route.params
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((data: Params) => (this.isCreateMode = _.isNil(data.id)));

    this.route.data
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((resolverData) => {
        const referentials: ReferentialsModel = resolverData.referentials;
        this.resolveAsset(resolverData.asset);
        this.resolveAssetTypes(referentials.AssetTypes);
        this.resolveRecipientEntities(resolverData.recipientEntities);
        if (!_.isNil(this.asset.OperationalGroupId)) {
          this.selectedItemsOperationalGroups = [
            new GroupModel(
              this.asset.OperationalGroupId,
              this.asset.OperationalGroupName
            ),
          ];
        }
      });
  }

  protected onGroupFetched(groupType: GroupTypeModel, groups: GroupModel[]) {
    this.operationalGroups = groups;
  }

  get IsFormValid(): boolean {
    return (
      !_.isNil(this.asset.Reference) &&
      this.asset.Reference.length > 0 &&
      !_.isNil(this.asset.TypeId) &&
      this.asset.TypeId > 0 &&
      (!this.IsRecipientEntityMandatory ||
        (!_.isNil(this.asset.RecipientEntityId) &&
          this.asset.RecipientEntityId > 0)) &&
      (!this.asset.IsActive ||
        (!_.isNil(this.asset.OperationalGroupId) &&
          this.asset.OperationalGroupId > 0))
    );
  }

  get IsRecipientEntityMandatory(): boolean {
    return (
      this.asset.TypeId == AssetTypeModel.BUILDING ||
      this.asset.TypeId == AssetTypeModel.OPERATION
    );
  }

  resolveAsset(asset: AssetModel): void {
    if (_.isNil(asset)) {
      this.asset = new AssetModel();
      this.asset.IsActive = false;
      return;
    }

    this.asset = asset;
  }

  resolveAssetTypes(assetTypes: ReferentialModel[]): void {
    if (_.isNil(assetTypes)) {
      return;
    }

    this.assetTypes = assetTypes.filter(
      (a) => a.Id != AssetTypeModel.ALL && a.Id != AssetTypeModel.COMPANY
    );
    this.selectedItemsAssetTypes = assetTypes.filter(
      (a) => a.Id == this.asset.TypeId
    );
  }

  resolveRecipientEntities(recipientEntities: RecipientEntityModel[]): void {
    if (_.isNil(recipientEntities)) {
      return;
    }

    this.recipientEntities = recipientEntities;
    this.selectedItemsRecipientEntities = recipientEntities.filter(
      (g) => g.Id == this.asset.RecipientEntityId
    );
  }

  onSelectAssetType(data: ReferentialModel, action: string): void {
    switch (action) {
      case 'select':
        this.asset.TypeId = data.Id;
        this.asset.TypeName = data.Label;
        break;
      case 'deselect':
        this.asset.TypeId = null;
        this.asset.TypeName = null;
        break;
    }
  }

  onSelectOperationalGroup(data: GroupModel, action: string): void {
    switch (action) {
      case 'select':
        this.asset.OperationalGroupId = data.Id;
        this.asset.OperationalGroupName = data.Name;
        break;
      case 'deselect':
        this.asset.OperationalGroupId = null;
        this.asset.OperationalGroupName = null;
        this.asset.IsActive = false;
        break;
    }
  }

  onSelectRecipientEntitiy(data: RecipientEntityModel, action: string): void {
    switch (action) {
      case 'select':
        this.asset.RecipientEntityId = data.Id;
        this.asset.RecipientEntityName = data.Name;
        break;
      case 'deselect':
        this.asset.RecipientEntityId = null;
        this.asset.RecipientEntityName = null;
        break;
    }
  }

  onSave(): void {
    this.submitted = true;
    if (this.IsFormValid) {
      this.onUpdate(this.isCreateMode)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((isConfirmed: boolean) => {
          if (isConfirmed) {
            this.onSaveConfirmed();
          }
        });
    }
  }

  onSaveConfirmed(): void {
    if (this.isCreateMode) {
      this.assetService
        .createAsset(this.asset)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (_) => {
            this.onCreateSuccess().subscribe((_) => this.navigateToAssetList());
          },
          () => {
            this.onCreateError();
          }
        );
      return;
    }

    this.assetService
      .updateAsset(this.asset)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (_) => {
          this.onUpdateSuccess().subscribe((_) => this.navigateToAssetList());
        },
        () => {
          this.onUpdateError();
        }
      );
  }

  onCancel() {
    this.navigateToAssetList();
  }

  private navigateToAssetList(): void {
    this.router.navigate(['administration/assets']);
  }

  ngOnDestroy() {
    this.unsubscribe$.next(true);
  }

  onUpdate(isCreationMode: boolean): Observable<any> {
    let verb = isCreationMode ? 'créer' : 'modifier';
    const confimModal = new ModalHelper().getConfirmationConfig(
      `Êtes-vous sûr de vouloir ${verb} cette référence ?`
    );
    const dialog = this.dialog.open(ModalComponent, confimModal);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  onUpdateSuccess(): Observable<any> {
    return this.onSuccess(PopUpMode.UPDATE);
  }

  onUpdateError(): Observable<any> {
    return this.onError(PopUpMode.UPDATE);
  }

  onCreateSuccess(): Observable<any> {
    return this.onSuccess(PopUpMode.CREATE);
  }

  onCreateError(): Observable<any> {
    return this.onError(PopUpMode.CREATE);
  }

  onSuccess(mode: PopUpMode): Observable<any> {
    const modalErreurConfig = new ModalHelper().getInformationConfig(
      `La ${mode} de la référence ${this.asset.Reference} a été effectuée avec succès.`
    );
    const dialog = this.dialog.open(ModalComponent, modalErreurConfig);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }

  onError(mode: PopUpMode): Observable<any> {
    const modalErreurConfig = new ModalHelper().getAlertConfig(
      `Une erreur est survenue lors de la ${mode} de la référence ${this.asset.Reference}.`
    );
    const dialog = this.dialog.open(ModalComponent, modalErreurConfig);

    return dialog.afterClosed().pipe(takeUntil(this.unsubscribe$));
  }
}
